import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Close as CloseIcon } from '@material-ui/icons';
import { Typography, IconButton, makeStyles, Dialog, DialogTitle, DialogContent } from '@material-ui/core';
import { useThemeMode } from '../../contexts/index.jsx';

/**
 * @function TideChartModal
 * @description
 * Modal dialog that renders a Chart.js tide-level chart.
 * Shows the full tide series from `fileData.tideData` as a grey background
 * series, and overlays the image-acquisition points (date + tide level)
 * collected during processing as red × markers.
 * Chart.js 4.4.1 and chartjs-adapter-luxon are injected as <script> tags
 * on first open and cached on the window object to avoid duplicate loads.
 * @param {Object}   props
 * @param {boolean}  props.open              - Controls dialog visibility.
 * @param {Function} props.onClose           - Callback to close the dialog.
 * @param {Array}    props.tideSeries        - Full tide series: [{ date: string|Date, level: number }]
 * @param {Array}    props.acquisitionPoints - Image acquisition points: [{ date: string, tideLevel: number }]
 * @param {string}   props.location          - Location label for the chart title (e.g. 'Santoña').
 * @see {ProcessingPage}
 * @memberof module:Bathymetry
 * @returns {JSX.Element} The dialog, empty until it is first opened.
 */
const TideChartModal = ({ open, onClose, tideSeries, acquisitionPoints, location }) => {
  const classes = useModalStyles();
  const [t] = useTranslation();
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const { shorelineThemeMode } = useThemeMode();

  useEffect(() => {
    if (!open) return;
    const renderChart = () => {
      if (!canvasRef.current) return;
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      const Chart = window.Chart;
      if (!Chart) return;
      const isDark = shorelineThemeMode === 'dark';
      const tickColor = isDark ? '#aaa' : '#555';
      const titleColor = isDark ? '#eee' : '#222';
      const gridColor = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)';
      const labelBg = isDark ? 'rgba(40,40,40,0.9)' : 'rgba(255,255,255,0.92)';
      const labelBorder = isDark ? '#999' : '#777';
      const dates = acquisitionPoints.map((p) => p.date).sort();
      const dateRange =
        dates.length >= 2 ? `${dates[0].replace(/-/g, '')}_${dates[dates.length - 1].replace(/-/g, '')}` : '';
      const PADDING_DAYS = 3;
      const ONE_DAY_MS = 1000 * 60 * 60 * 24;
      let axisMin = null;
      let axisMax = null;
      if (dates.length > 0) {
        axisMin = new Date(dates[0]).getTime() - PADDING_DAYS * ONE_DAY_MS;
        axisMax = new Date(dates[dates.length - 1]).getTime() + PADDING_DAYS * ONE_DAY_MS;
      }
      const tideData = (tideSeries || [])
        .map((p) => ({
          x: new Date(p.date).getTime(),
          y: parseFloat(p.level),
        }))
        .filter((p) => axisMin == null || (p.x >= axisMin && p.x <= axisMax));
      const acqData = (acquisitionPoints || []).map((p) => ({
        x: new Date(p.date).getTime(),
        y: parseFloat(p.tideLevel),
      }));
      const levels = tideData.map((p) => p.y);
      const seriesMin = levels.length ? Math.min(...levels).toFixed(2) : '—';
      const seriesMax = levels.length ? Math.max(...levels).toFixed(2) : '—';
      let meanInterval = '—';
      if (dates.length >= 2) {
        const diffs = [];
        for (let i = 1; i < dates.length; i++) {
          diffs.push((new Date(dates[i]) - new Date(dates[i - 1])) / (1000 * 60 * 60 * 24));
        }
        meanInterval = Math.round(diffs.reduce((a, b) => a + b, 0) / diffs.length);
      }
      const valueLabelsPlugin = {
        id: 'valueLabels',
        afterDatasetDraw(chart, args) {
          if (args.index !== 1) return;
          const { ctx } = chart;
          const meta = chart.getDatasetMeta(1);
          meta.data.forEach((point, i) => {
            const val = acqData[i]?.y;
            if (val == null) return;
            const label = val.toFixed(2);
            const x = point.x;
            const y = point.y;
            const offsetY = val >= 0 ? -20 : 20;
            const boxW = 40,
              boxH = 18,
              boxR = 3;
            ctx.save();
            ctx.beginPath();
            ctx.roundRect(x - boxW / 2, y + offsetY - boxH / 2, boxW, boxH, boxR);
            ctx.fillStyle = labelBg;
            ctx.strokeStyle = labelBorder;
            ctx.lineWidth = 0.8;
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = isDark ? '#eee' : '#111';
            ctx.font = '11px sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(label, x, y + offsetY);
            ctx.restore();
          });
        },
      };
      chartRef.current = new Chart(canvasRef.current.getContext('2d'), {
        type: 'line',
        data: {
          datasets: [
            {
              label: t('forms.acquisition.5.bathymetry.tideChart.seriesLabel', {
                min: seriesMin,
                max: seriesMax,
              }),
              data: tideData,
              borderColor: isDark ? 'rgba(200,200,200,0.5)' : 'rgba(130,130,130,0.55)',
              borderWidth: 1,
              pointRadius: 0,
              tension: 0.3,
              fill: false,
              parsing: false,
              order: 2,
            },
            {
              label: t('forms.acquisition.5.bathymetry.tideChart.acquisitionLabel', {
                days: meanInterval,
              }),
              data: acqData,
              borderColor: '#c0392b',
              backgroundColor: '#c0392b',
              borderWidth: 1.8,
              pointRadius: 7,
              pointStyle: 'crossRot',
              pointBorderWidth: 2.2,
              tension: 0,
              fill: false,
              parsing: false,
              order: 1,
            },
          ],
        },
        options: {
          animation: false,
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'nearest', axis: 'x', intersect: false },
          plugins: {
            legend: {
              display: true,
              position: 'bottom',
              align: 'start',
              labels: {
                color: tickColor,
                font: { size: 11 },
                padding: 16,
                usePointStyle: true,
                pointStyleWidth: 14,
                boxHeight: 10,
              },
            },
            title: {
              display: true,
              text: `${t('forms.acquisition.5.bathymetry.tideChart.chartTitle')}${location ? ` — ${location}` : ''}${dateRange ? ` (${dateRange})` : ''}`,
              color: titleColor,
              font: { size: 13, weight: '500' },
              padding: { bottom: 12 },
            },
            tooltip: {
              callbacks: {
                label: (ctx) => ` ${ctx.parsed.y.toFixed(2)} m`,
                title: (items) => new Date(items[0].parsed.x).toISOString().slice(0, 10),
              },
            },
          },
          scales: {
            x: {
              type: 'time',
              min: axisMin ?? undefined,
              max: axisMax ?? undefined,
              time: {
                unit: 'day',
                tooltipFormat: 'yyyy-MM-dd',
                displayFormats: { day: 'yyyy-MM-dd' },
              },
              ticks: { color: tickColor, font: { size: 11 }, maxTicksLimit: 5 },
              grid: { display: true, color: gridColor, lineWidth: 1 },
              border: { color: gridColor },
            },
            y: {
              title: {
                display: true,
                text: t('forms.acquisition.5.bathymetry.tideChart.yAxisLabel'),
                color: tickColor,
                font: { size: 11 },
              },
              ticks: { color: tickColor, font: { size: 11 }, stepSize: 1 },
              grid: { display: true, color: gridColor, lineWidth: 1 },
              border: { color: gridColor },
            },
          },
        },
        plugins: [valueLabelsPlugin],
      });
    };
    const loadScripts = () => {
      if (window.Chart && window.__chartjsLuxonLoaded) {
        renderChart();
        return;
      }
      const loadScript = (src) =>
        new Promise((resolve, reject) => {
          const existing = document.querySelector(`script[src="${src}"]`);
          if (existing) {
            resolve();
            return;
          }
          const s = document.createElement('script');
          s.src = src;
          s.onload = resolve;
          s.onerror = reject;
          document.head.appendChild(s);
        });
      loadScript('https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.min.js')
        .then(() => loadScript('https://cdn.jsdelivr.net/npm/luxon@3.4.4/build/global/luxon.min.js'))
        .then(() =>
          loadScript('https://cdn.jsdelivr.net/npm/chartjs-adapter-luxon@1.3.1/dist/chartjs-adapter-luxon.umd.min.js'),
        )
        .then(() => {
          window.__chartjsLuxonLoaded = true;
          renderChart();
        })
        .catch((err) => console.error('[TideChartModal] script load error:', err));
    };
    const timer = setTimeout(loadScripts, 80);
    return () => clearTimeout(timer);
  }, [open, tideSeries, acquisitionPoints, location, t, shorelineThemeMode]);

  useEffect(() => {
    return () => {
      if (!chartRef.current) return;
      chartRef.current.destroy();
      chartRef.current = null;
    };
  }, []);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth classes={{ paper: classes.dialogPaper }}>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          {t('forms.acquisition.5.bathymetry.tideChart.dialogTitle')}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <canvas ref={canvasRef} style={{ width: '100%', height: '320px', display: 'block' }} />
      </DialogContent>
    </Dialog>
  );
};

const useModalStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 12,
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 20px',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  dialogContent: {
    padding: '20px',
  },
}));

export default TideChartModal;
